import { SectionStatus } from './types';

export const getStatusText = (status: SectionStatus): string => {
  switch (status) {
    case 'complete':
      return 'Complete';
    case 'in_progress':
      return 'In Progress';
    case 'optional':
      return 'Optional';
    case 'not_started':
    default:
      return 'Not Started';
  }
};

export const getClarificationText = (
  unresolvedCount: number,
  _resolvedCount: number,
  _totalCount: number
): string => {
  if (unresolvedCount === 0) return 'Complete';
  return `${unresolvedCount} Clarification${unresolvedCount === 1 ? '' : 's'} Open`;
};

export interface SectionLabel {
  /** Drives colour and the sidebar icon */
  tone: SectionStatus;
  text: string;
}

/**
 * What a section's status badge says. Two phases, two vocabularies:
 *
 * - Filling in (draft): form-completion — Not Started / In Progress /
 *   Complete / Optional.
 * - After submission: completion is moot (the form was complete to be
 *   submitted), so only clarifications are worth labelling — "N
 *   Clarifications Open" while any are unresolved, "Complete" once they are
 *   all resolved, and no label at all for a section that never had any.
 *
 * `null` means render no badge.
 */
export const getSectionLabel = ({
  isFillingIn,
  status,
  unresolvedCount,
  totalClarificationCount,
}: {
  isFillingIn: boolean;
  status: SectionStatus;
  unresolvedCount: number;
  totalClarificationCount: number;
}): SectionLabel | null => {
  if (totalClarificationCount > 0) {
    return unresolvedCount > 0
      ? {
          tone: 'in_progress',
          text: getClarificationText(
            unresolvedCount,
            totalClarificationCount - unresolvedCount,
            totalClarificationCount
          ),
        }
      : { tone: 'complete', text: 'Complete' };
  }
  if (!isFillingIn) return null;
  return { tone: status, text: getStatusText(status) };
};

/**
 * The form is being filled in while it's a draft. Without submission data
 * (e.g. a template preview) it's treated the same way.
 */
export const isFillingInStatus = (
  submissionStatus: string | null | undefined
): boolean => !submissionStatus || submissionStatus === 'draft';

export const getStatusBadgeClasses = (status: SectionStatus): string => {
  switch (status) {
    case 'complete':
      return 'bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium';
    case 'in_progress':
      return 'bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs font-medium';
    case 'optional':
      return 'bg-blue-50 text-blue-500 px-2 py-1 rounded-full text-xs font-medium';
    case 'not_started':
    default:
      return 'bg-gray-100 text-gray-500 px-2 py-1 rounded-full text-xs font-medium';
  }
};
